import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { Button, Card, Input, Select, Badge, Modal, LoadingSpinner, Pagination, ErrorSummary } from '@/components/common';
import { useBulkSelection, BulkActionBar, BulkSelectAllCheckbox, BulkSelectCheckbox } from '@/components/bulk';
import api, { getErrorMessage } from '@/services/api';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type { PaginatedResponse } from '@/types';

/**
 * Product Groups, with their sub-groups mapped in place (COUNTRY-5).
 *
 * This was a declarative `AdminCrudPage`, which cannot express a parent/child
 * relationship — so a group's sub-groups could only be reached from a separate
 * Product Subgroups screen, and there was no way to see which group had what.
 * Groups now expand to show their sub-groups and take a new one inline.
 *
 * Two of the five columns were also permanently blank, because the config read
 * field names the API does not send: `insurance_type_name` where the serializer
 * sends `type_name`, and `subgroup_count` where it sends `subgroups_count`.
 * That is the sort of thing a declarative config hides — there is no type
 * connecting the two ends, so a wrong key renders empty rather than failing.
 *
 * Item 7 (2026-09-18 batch) adds back what the move off `AdminCrudPage` cost
 * this screen -- checkboxes and bulk actions -- via the same shared bulk kit
 * the pages that stayed on `AdminCrudPage` get for free (`useBulkSelection` +
 * `BulkActionBar`, backed by `InsuranceGroupViewSet`'s `POST bulk/`), a Status
 * filter (the model already has `is_active` and the viewset already filters
 * on it), and a View popup. None of that touches the expand/collapse
 * parent/child behaviour above, which is the whole reason this page is not
 * back on `AdminCrudPage`.
 */

interface InsuranceGroup {
  id: string;
  name: string;
  code: string;
  description: string;
  insurance_type: string;
  /** NOT `insurance_type_name` — see the note above. */
  type_name: string;
  display_order: number;
  is_active: boolean;
  /** NOT `subgroup_count`. */
  subgroups_count: number;
}

interface InsuranceSubgroup {
  id: string;
  name: string;
  code: string;
  description: string;
  group: string;
  is_active: boolean;
  products_count: number;
}

interface Lookup {
  id: string;
  name: string;
}

const itemsPerPage = 20;
const emptyGroup = { name: '', description: '', insurance_type: '', is_active: true };

export function ProductGroupsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [filterType, setFilterType] = useState('');
  // Item 7: Status, alongside the existing Insurance Type filter. `is_active`
  // is a real column on the model and is already in
  // `InsuranceGroupViewSet.filterset_fields`, so this is a plain query param.
  const [filterActive, setFilterActive] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewTarget, setViewTarget] = useState<InsuranceGroup | null>(null);
  const [formData, setFormData] = useState(emptyGroup);

  const { data: types = [] } = useQuery<Lookup[]>({
    queryKey: ['insurance-types-lookup'],
    queryFn: async () => {
      // page_size is explicit: the default page size is 20 and the type list is
      // already at 13. Without this the dropdown would start silently dropping
      // types the moment a fourteenth is added, which is exactly the failure
      // the product form was reported for.
      const res = await api.get('/products/types/', { params: { page_size: 200 } });
      return res.data.results || res.data;
    },
  });

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery<PaginatedResponse<InsuranceGroup>>({
    queryKey: ['product-groups', page, debouncedSearch, filterType, filterActive],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(itemsPerPage) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterType) params.set('insurance_type', filterType);
      if (filterActive) params.set('is_active', filterActive);
      const res = await api.get(`/products/groups/?${params.toString()}`);
      return res.data;
    },
  });

  const groups = data?.results ?? [];
  const totalPages = Math.ceil((data?.count ?? 0) / itemsPerPage);
  const visibleIds = groups.map((g) => g.id);
  const bulkSelection = useBulkSelection(visibleIds);

  // Only the expanded group's sub-groups are fetched — one request per opened
  // row rather than an N+1 sweep on every page render.
  const { data: subgroups = [], isFetching: subgroupsLoading } = useQuery<InsuranceSubgroup[]>({
    queryKey: ['product-subgroups', expandedId],
    queryFn: async () => {
      const res = await api.get('/products/subgroups/', {
        params: { group: expandedId, page_size: 200 },
      });
      return res.data.results || res.data;
    },
    enabled: !!expandedId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['product-groups'] });
    queryClient.invalidateQueries({ queryKey: ['product-subgroups'] });
    // The product form's cascade reads these same lists.
    queryClient.invalidateQueries({ queryKey: ['insurance-groups-lookup'] });
    queryClient.invalidateQueries({ queryKey: ['insurance-subgroups-lookup'] });
  };

  const saveGroup = useMutation({
    mutationFn: async (payload: typeof emptyGroup) => {
      if (editingId) return (await api.patch(`/products/groups/${editingId}/`, payload)).data;
      return (await api.post('/products/groups/', payload)).data;
    },
    onSuccess: () => {
      invalidate();
      toast.success(editingId ? 'Group updated' : 'Group created');
      setShowModal(false);
      setEditingId(null);
      setFormData(emptyGroup);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteGroup = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/products/groups/${id}/`);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Group deleted');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const addSubgroup = useMutation({
    // `code` is deliberately not sent: InsuranceSubgroupCreateUpdateSerializer
    // generates one from the name and guarantees it unique within the group.
    mutationFn: async (payload: { group: string; name: string }) =>
      (await api.post('/products/subgroups/', payload)).data,
    onSuccess: () => {
      invalidate();
      toast.success('Sub-group added');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteSubgroup = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/products/subgroups/${id}/`);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Sub-group removed');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const openEdit = (group: InsuranceGroup) => {
    setEditingId(group.id);
    setFormData({
      name: group.name,
      description: group.description || '',
      insurance_type: group.insurance_type,
      is_active: group.is_active,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(emptyGroup);
  };

  const typeOptions = types.map((t) => ({ value: t.id, label: t.name }));

  /**
   * Bulk Export -- the one thing the shared `BulkActionBar` does not offer
   * (it only knows Activate/Deactivate/Delete). Mirrors `AdminCrudPage`'s own
   * `handleExport`: a client-side CSV of the rows currently on screen, gated
   * behind having a selection the same way that one is gated behind the bulk
   * bar appearing at all.
   */
  const handleExportSelected = () => {
    const headers = ['Name', 'Code', 'Insurance Type', 'Description', 'Sub-groups', 'Status'].join(',');
    const csvCell = (value: unknown) => {
      const str = String(value ?? '');
      return str.includes(',') || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const rows = groups.map((g) =>
      [g.name, g.code, g.type_name || '', g.description || '', g.subgroups_count ?? 0, g.is_active ? 'Active' : 'Inactive']
        .map(csvCell)
        .join(',')
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product-groups-export.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Product Groups</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Groups and the sub-groups mapped under them
          </p>
        </div>
        <Button variant="primary" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setShowModal(true)}>
          Add Group
        </Button>
      </div>

      <Card padding="md">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="sm:col-span-2">
            <Input
              label="Search"
              placeholder="Search groups..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <Select
            label="Insurance Type"
            options={[{ value: '', label: 'All Insurance Types' }, ...typeOptions]}
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setPage(1);
            }}
          />
          <Select
            label="Status"
            options={[
              { value: '', label: 'All statuses' },
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ]}
            value={filterActive}
            onChange={(e) => {
              setFilterActive(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </Card>

      {/* Fixed-height slot: selecting a row must not shift the page. */}
      <div data-testid="bulk-action-slot" className="min-h-[3.5rem]">
        {bulkSelection.count > 0 && (
          <div className="flex flex-wrap items-start gap-2">
            <div className="min-w-0 flex-1">
              <BulkActionBar
                resource="products/groups"
                selectedIds={bulkSelection.selectedIds}
                entityName="groups"
                onChanged={() => queryClient.invalidateQueries({ queryKey: ['product-groups'] })}
                onClear={bulkSelection.clear}
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleExportSelected}>
              Export CSV
            </Button>
          </div>
        )}
      </div>

      {isError && <ErrorSummary messages={[getErrorMessage(error)]} />}

      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Could not load product groups</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Try again in a moment.</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No product groups found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Add a group to get started.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="w-10 px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <BulkSelectAllCheckbox
                        checked={bulkSelection.allVisibleSelected}
                        indeterminate={bulkSelection.someVisibleSelected}
                        onChange={bulkSelection.toggleAllVisible}
                      />
                    </th>
                    <th className="w-10 px-4 py-3" />
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Insurance Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Sub-groups</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {groups.map((group) => {
                    const expanded = expandedId === group.id;
                    return (
                      <>
                        <tr
                          key={group.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                          onClick={() => setExpandedId(expanded ? null : group.id)}
                        >
                          <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                            <BulkSelectCheckbox
                              checked={bulkSelection.isSelected(group.id)}
                              onChange={() => bulkSelection.toggle(group.id)}
                              label={`Select ${group.name}`}
                            />
                          </td>
                          <td className="px-4 py-4 text-gray-400">
                            {expanded ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" />
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{group.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{group.type_name || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{group.description || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{group.subgroups_count ?? 0}</td>
                          <td className="px-6 py-4">
                            <Badge variant={group.is_active ? 'success' : 'warning'}>
                              {group.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => setViewTarget(group)} title="View">
                                <EyeIcon className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openEdit(group)}>
                                <PencilIcon className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (window.confirm(`Delete "${group.name}"? This cannot be undone.`)) {
                                    deleteGroup.mutate(group.id);
                                  }
                                }}
                              >
                                <TrashIcon className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {expanded && (
                          <tr key={`${group.id}-subgroups`} className="bg-gray-50 dark:bg-gray-900/40">
                            <td />
                            <td />
                            <td colSpan={6} className="px-6 py-4">
                              <SubgroupPanel
                                group={group}
                                subgroups={subgroups}
                                loading={subgroupsLoading}
                                onAdd={(name) => addSubgroup.mutate({ group: group.id, name })}
                                adding={addSubgroup.isPending}
                                onDelete={(id) => deleteSubgroup.mutate(id)}
                              />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={data?.count ?? 0}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal isOpen={showModal} onClose={closeModal} title={editingId ? 'Edit Group' : 'Add Group'}>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            saveGroup.mutate(formData);
          }}
        >
          <Input
            label="Name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <Select
            label="Insurance Type"
            required
            options={typeOptions}
            value={formData.insurance_type}
            onChange={(e) => setFormData({ ...formData, insurance_type: e.target.value })}
            placeholder="Select an insurance type"
          />
          <Input
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
            Active
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" isLoading={saveGroup.isPending}>
              {editingId ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!viewTarget}
        onClose={() => setViewTarget(null)}
        title={viewTarget?.name || 'Group details'}
        size="md"
      >
        {viewTarget && (
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="font-medium text-gray-500 dark:text-gray-400">Name</dt>
              <dd className="text-gray-900 dark:text-white">{viewTarget.name}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500 dark:text-gray-400">Code</dt>
              <dd className="text-gray-900 dark:text-white">{viewTarget.code}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500 dark:text-gray-400">Insurance Type</dt>
              <dd className="text-gray-900 dark:text-white">{viewTarget.type_name || '-'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500 dark:text-gray-400">Description</dt>
              <dd className="whitespace-pre-wrap text-gray-900 dark:text-white">
                {viewTarget.description || 'No description.'}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500 dark:text-gray-400">Status</dt>
              <dd className="text-gray-900 dark:text-white">{viewTarget.is_active ? 'Active' : 'Inactive'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500 dark:text-gray-400">Sub-groups</dt>
              <dd className="text-gray-900 dark:text-white">{viewTarget.subgroups_count ?? 0}</dd>
            </div>
          </dl>
        )}
      </Modal>
    </div>
  );
}

/** The sub-groups mapped under one group, plus the field that adds another. */
function SubgroupPanel({
  group,
  subgroups,
  loading,
  onAdd,
  adding,
  onDelete,
}: {
  group: InsuranceGroup;
  subgroups: InsuranceSubgroup[];
  loading: boolean;
  onAdd: (name: string) => void;
  adding: boolean;
  onDelete: (id: string) => void;
}) {
  const [name, setName] = useState('');

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setName('');
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Sub-groups of {group.name}
      </p>

      {loading ? (
        <p className="text-sm text-gray-500">Loading sub-groups…</p>
      ) : subgroups.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No sub-groups mapped yet. Add one below — it becomes selectable on the product form.
        </p>
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          {subgroups.map((sub) => (
            <li key={sub.id} className="flex items-center justify-between px-3 py-2">
              <div className="min-w-0">
                <span className="text-sm text-gray-900 dark:text-white">{sub.name}</span>
                <span className="ml-2 font-mono text-xs text-gray-400">{sub.code}</span>
                {sub.products_count > 0 && (
                  <span className="ml-2 text-xs text-gray-500">
                    {sub.products_count} product{sub.products_count === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Named in the prompt: a sub-group carrying products is the
                  // one worth pausing over, and the count is right there.
                  if (window.confirm(`Remove sub-group "${sub.name}"?`)) onDelete(sub.id);
                }}
              >
                <TrashIcon className="w-4 h-4 text-red-500" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1 max-w-xs">
          <Input
            label="Add sub-group"
            placeholder="e.g. Comprehensive"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
          />
        </div>
        <Button size="sm" onClick={submit} isLoading={adding} disabled={!name.trim()}>
          Add
        </Button>
      </div>
    </div>
  );
}
